import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Trans, useTranslation } from 'react-i18next';
import { IconArrowLeft, IconCheck, IconX } from '@tabler/icons-react';
import { newRequestId } from '@moneytome/shared';
import { Button, Select } from '../../shared/ui';
import { legalDocs } from '../../shared/lib/legal-docs';
import successCheck from '../../shared/assets/images/success-check.svg';
import successEffect from '../../shared/assets/images/success-effect.svg';
import { submitApplication } from './api';
import { applyAnalyticsEvents, emitApplyEvent } from './analytics';
import { businessCategories, cities } from './dictionaries';
import { buildApplicationRequest } from './payload';
import { PHONE_MASK_MAX_LENGTH, applyPhoneInput } from './phone';
import { applySchema, businessTypes } from './schema';
import type { ReactNode } from 'react';
import type { FieldErrors } from 'react-hook-form';
import type { ApplyFormPayload, ApplyFormValues } from './schema';

export interface ApplyModalHandle {
  open: () => void;
}

const inputClass =
  'h-14 w-full rounded-lg border border-border-strong bg-surface px-4 text-base text-ink placeholder:text-text-placeholder focus:border-accent focus:outline-none aria-[invalid=true]:border-red-600';

const labelClass = 'text-sm font-medium text-text-tertiary';

function Field({
  id,
  label,
  error,
  children,
  className = '',
}: {
  id: string;
  label: string;
  error?: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={`flex flex-1 flex-col gap-2 ${className}`}>
      <label id={`${id}-label`} htmlFor={id} className={labelClass}>
        {label}
      </label>
      {children}
      {error ? (
        <span id={`${id}-error`} role="alert" className="text-sm text-red-600">
          {error}
        </span>
      ) : null}
    </div>
  );
}

/** aria-describedby target only when there is an active error. */
const describedBy = (id: string, error?: string) => (error ? `${id}-error` : undefined);

export const ApplyModal = forwardRef<ApplyModalHandle>(function ApplyModalImpl(_props, ref) {
  const { t } = useTranslation();
  const dialogRef = useRef<HTMLDialogElement>(null);
  const idempotencyKeyRef = useRef(newRequestId());
  const fillStartedRef = useRef(false);
  const [view, setView] = useState<'form' | 'success'>('form');
  const [submitError, setSubmitError] = useState<string | null>(null);

  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<ApplyFormValues, unknown, ApplyFormPayload>({
    resolver: zodResolver(applySchema),
    defaultValues: {
      contactName: '',
      businessName: '',
      phone: '',
      email: '',
      businessType: 'legal_entity',
      bin: '',
      category: '',
      locationsCount: '',
      cities: 'ALMATY',
      description: '',
      consent: false,
    },
  });

  const close = () => dialogRef.current?.close();

  useImperativeHandle(ref, () => ({
    open: () => {
      setView('form');
      setSubmitError(null);
      fillStartedRef.current = false;
      // New idempotency key per fresh form open; retries of one submission
      // reuse the same key (it is only reset here, never in onSubmit).
      idempotencyKeyRef.current = newRequestId();
      reset();
      document.body.classList.add('overflow-hidden');
      dialogRef.current?.showModal();
      emitApplyEvent(applyAnalyticsEvents.formOpened);
    },
  }));

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    const onClose = () => document.body.classList.remove('overflow-hidden');
    dialog.addEventListener('close', onClose);
    return () => {
      dialog.removeEventListener('close', onClose);
      // Route navigation can unmount an open dialog without emitting `close`.
      onClose();
    };
  }, []);

  const onFirstInput = () => {
    if (fillStartedRef.current) return;
    fillStartedRef.current = true;
    emitApplyEvent(applyAnalyticsEvents.fillStarted);
  };

  const onValid = async (data: ApplyFormPayload) => {
    setSubmitError(null);
    emitApplyEvent(applyAnalyticsEvents.submitAttempted);
    try {
      await submitApplication(buildApplicationRequest(data), idempotencyKeyRef.current);
      emitApplyEvent(applyAnalyticsEvents.submitSucceeded);
      setView('success');
    } catch (error) {
      // Categorical only — never the message/payload (privacy-safe telemetry).
      const errorCategory = error instanceof Error ? error.name : 'unknown';
      emitApplyEvent(applyAnalyticsEvents.submitFailed, { errorCategory });
      setSubmitError(t('applyForm.errors.submit'));
    }
  };

  const onInvalid = (formErrors: FieldErrors<ApplyFormValues>) => {
    // Field categories only (names), never the entered values.
    emitApplyEvent(applyAnalyticsEvents.validationFailed, {
      fields: Object.keys(formErrors),
    });
  };

  const onSubmit = handleSubmit(onValid, onInvalid);

  const errorMessage = (key?: string) => (key ? t(key) : undefined);

  return (
    <dialog
      ref={dialogRef}
      aria-labelledby="apply-modal-title"
      className={`apply-sheet sm:rounded-t-card-lg sm:rounded-b-card-lg m-0 w-full max-w-none overflow-y-auto bg-white p-0 backdrop:bg-[#fafafab8] sm:m-auto sm:max-h-[90vh] sm:max-w-[600px] ${
        view === 'form'
          ? // Mobile: full-screen form.
            'h-[100dvh] max-h-none rounded-none sm:h-auto'
          : // Mobile: partial bottom sheet that hugs its content.
            // From sm up an explicit fit-content height is required: a modal dialog is
            // `position: fixed; inset: 0` per UA styles, so `height: auto` stretches
            // between top and bottom and then clamps to max-height (90vh) instead of
            // hugging the short success content.
            'rounded-t-card mt-auto max-h-[92vh] rounded-b-none sm:h-fit'
      }`}
      onClick={(event) => {
        if (event.target === dialogRef.current) close();
      }}
      onMouseDown={(event) => {
        // Prevent text selection drag from registering as a backdrop click.
        if (event.target === dialogRef.current) event.preventDefault();
      }}
    >
      {view === 'form' ? (
        // On mobile the bottom padding is dropped so the sticky submit footer
        // alone defines a constant gap from the bottom edge (stuck or at rest).
        <div className="flex flex-col gap-6 p-6 max-sm:pb-0">
          <div className="flex flex-col gap-2">
            <div className="relative flex items-center justify-center sm:justify-between">
              {/* Mobile: circular back button, absolutely placed left of the centered title. */}
              <button
                type="button"
                onClick={close}
                aria-label={t('applyForm.back')}
                className="bg-canvas text-ink absolute left-0 flex size-10 items-center justify-center rounded-full sm:hidden"
              >
                <IconArrowLeft className="size-5" aria-hidden />
              </button>
              <h2
                id="apply-modal-title"
                className="text-text-primary text-lg font-semibold sm:text-2xl sm:font-medium"
              >
                {t('applyForm.title')}
              </h2>
              {/* Desktop: close (X) on the right. */}
              <button
                type="button"
                onClick={close}
                aria-label={t('applyForm.close')}
                className="text-text-placeholder hover:text-ink hidden sm:block"
              >
                <IconX className="size-8" aria-hidden />
              </button>
            </div>
            <p className="text-text-placeholder hidden text-base sm:block">
              {t('applyForm.description')}
            </p>
          </div>

          {/* `ym-disable-keys` stops Yandex.Metrika's Webvisor from recording what
              is typed into this form — phone, email and BIN are personal data.
              The class sits on the form, not on individual inputs, so a field
              added later is covered by default. The counter's panel setting is
              not a substitute: the guarantee has to live in the repo (see the
              strict PII rule in `analytics.ts`). */}
          <form
            className="ym-disable-keys flex flex-col gap-4"
            onSubmit={onSubmit}
            onInput={onFirstInput}
            noValidate
          >
            <div className="flex flex-col gap-4 sm:flex-row">
              <Field
                id="apply-contactName"
                label={t('applyForm.fields.contactName')}
                error={errorMessage(errors.contactName?.message)}
              >
                <input
                  id="apply-contactName"
                  className={inputClass}
                  placeholder={t('applyForm.fields.contactNamePlaceholder')}
                  aria-invalid={errors.contactName ? true : undefined}
                  aria-describedby={describedBy('apply-contactName', errors.contactName?.message)}
                  {...register('contactName')}
                />
              </Field>
              <Field
                id="apply-businessName"
                label={t('applyForm.fields.businessName')}
                error={errorMessage(errors.businessName?.message)}
              >
                <input
                  id="apply-businessName"
                  className={inputClass}
                  placeholder={t('applyForm.fields.businessNamePlaceholder')}
                  aria-invalid={errors.businessName ? true : undefined}
                  aria-describedby={describedBy('apply-businessName', errors.businessName?.message)}
                  {...register('businessName')}
                />
              </Field>
            </div>

            <div className="flex flex-col gap-4 sm:flex-row">
              <Field
                id="apply-phone"
                label={t('applyForm.fields.phone')}
                error={errorMessage(errors.phone?.message)}
              >
                <Controller
                  name="phone"
                  control={control}
                  render={({ field }) => (
                    <input
                      id="apply-phone"
                      type="tel"
                      autoComplete="tel"
                      inputMode="tel"
                      maxLength={PHONE_MASK_MAX_LENGTH}
                      className={inputClass}
                      placeholder={t('applyForm.fields.phonePlaceholder')}
                      aria-invalid={errors.phone ? true : undefined}
                      aria-describedby={describedBy('apply-phone', errors.phone?.message)}
                      name={field.name}
                      ref={field.ref}
                      value={field.value}
                      onBlur={field.onBlur}
                      onChange={(event) =>
                        field.onChange(applyPhoneInput(field.value, event.target.value))
                      }
                    />
                  )}
                />
              </Field>
              <Field
                id="apply-email"
                label={t('applyForm.fields.email')}
                error={errorMessage(errors.email?.message)}
              >
                <input
                  id="apply-email"
                  type="email"
                  autoComplete="email"
                  className={inputClass}
                  placeholder={t('applyForm.fields.emailPlaceholder')}
                  aria-invalid={errors.email ? true : undefined}
                  aria-describedby={describedBy('apply-email', errors.email?.message)}
                  {...register('email')}
                />
              </Field>
            </div>

            <fieldset className="flex flex-col gap-2">
              <legend className={labelClass}>{t('applyForm.fields.businessType')}</legend>
              <div className="flex gap-4">
                {businessTypes.map((type) => (
                  <label
                    key={type}
                    className="border-border text-ink has-[:checked]:border-accent has-[:checked]:bg-accent-subtle has-[:checked]:text-accent flex-1 cursor-pointer rounded-lg border px-3.5 py-2 text-center text-base font-medium"
                  >
                    <input
                      type="radio"
                      value={type}
                      className="sr-only"
                      {...register('businessType')}
                    />
                    {t(
                      type === 'legal_entity'
                        ? 'applyForm.fields.businessTypeLegalEntity'
                        : 'applyForm.fields.businessTypeIndividualEntrepreneur',
                    )}
                  </label>
                ))}
              </div>
            </fieldset>

            <Field
              id="apply-bin"
              label={t('applyForm.fields.bin')}
              error={errorMessage(errors.bin?.message)}
            >
              <input
                id="apply-bin"
                inputMode="numeric"
                maxLength={12}
                className={inputClass}
                placeholder={t('applyForm.fields.binPlaceholder')}
                aria-invalid={errors.bin ? true : undefined}
                aria-describedby={describedBy('apply-bin', errors.bin?.message)}
                {...register('bin')}
              />
            </Field>

            <Field
              id="apply-category"
              label={t('applyForm.fields.category')}
              error={errorMessage(errors.category?.message)}
            >
              <Controller
                name="category"
                control={control}
                render={({ field }) => (
                  <Select
                    id="apply-category"
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    options={businessCategories.map((category) => ({
                      value: category.id,
                      label: t(category.labelKey),
                    }))}
                    placeholder={t('applyForm.fields.categoryPlaceholder')}
                    aria-invalid={errors.category ? true : undefined}
                    aria-describedby={describedBy('apply-category', errors.category?.message)}
                    aria-labelledby={`apply-category-label apply-category`}
                  />
                )}
              />
            </Field>

            <div className="flex flex-col gap-4 sm:flex-row">
              <Field
                id="apply-locationsCount"
                label={t('applyForm.fields.locationsCount')}
                error={errorMessage(errors.locationsCount?.message)}
              >
                <input
                  id="apply-locationsCount"
                  type="number"
                  min={1}
                  inputMode="numeric"
                  className={inputClass}
                  placeholder={t('applyForm.fields.locationsCountPlaceholder')}
                  aria-invalid={errors.locationsCount ? true : undefined}
                  aria-describedby={describedBy(
                    'apply-locationsCount',
                    errors.locationsCount?.message,
                  )}
                  {...register('locationsCount')}
                />
              </Field>
              <Field
                id="apply-cities"
                label={t('applyForm.fields.cities')}
                error={errorMessage(errors.cities?.message)}
              >
                <Controller
                  name="cities"
                  control={control}
                  render={({ field }) => (
                    <Select
                      id="apply-cities"
                      value={field.value}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      options={cities.map((city) => ({
                        value: city.id,
                        label: t(city.labelKey),
                      }))}
                      aria-invalid={errors.cities ? true : undefined}
                      aria-describedby={describedBy('apply-cities', errors.cities?.message)}
                      aria-labelledby={`apply-cities-label apply-cities`}
                    />
                  )}
                />
              </Field>
            </div>

            <Field id="apply-description" label={t('applyForm.fields.description')}>
              <textarea
                id="apply-description"
                rows={2}
                maxLength={500}
                className={`${inputClass} h-auto resize-none py-4`}
                placeholder={t('applyForm.fields.descriptionPlaceholder')}
                {...register('description')}
              />
            </Field>

            <label className="flex cursor-pointer items-start gap-3">
              <span className="relative mt-0.5 inline-flex size-6 shrink-0">
                <input
                  type="checkbox"
                  className="peer sr-only"
                  aria-invalid={errors.consent ? true : undefined}
                  aria-describedby={describedBy('apply-consent', errors.consent?.message)}
                  {...register('consent')}
                />
                <span
                  aria-hidden
                  className="border-border-strong bg-surface peer-checked:border-accent peer-checked:bg-accent peer-focus-visible:ring-accent/40 pointer-events-none absolute inset-0 rounded-md border transition-colors peer-focus-visible:ring-2 peer-focus-visible:ring-offset-2"
                />
                <IconCheck
                  aria-hidden
                  stroke={2.5}
                  className="pointer-events-none absolute inset-0 m-auto size-4 text-white opacity-0 transition-opacity peer-checked:opacity-100"
                />
              </span>
              {/* The policy link is nested in the label on purpose: browsers skip
                  label activation for interactive descendants, so opening the
                  policy leaves the consent box untouched (verified in Chrome —
                  jsdom does not model that rule, hence no test for it). It stays
                  `target="_blank"`: the policy page is outside the SPA, so a
                  same-tab navigation would drop the half-filled form. */}
              <span className="text-text-tertiary text-sm">
                <Trans
                  i18nKey="applyForm.consent"
                  components={{
                    policy: (
                      <a
                        href={legalDocs.privacy.path}
                        target="_blank"
                        rel="noreferrer"
                        className="underline hover:no-underline"
                      />
                    ),
                  }}
                />
              </span>
            </label>
            {errorMessage(errors.consent?.message) ? (
              <span id="apply-consent-error" role="alert" className="-mt-2 text-sm text-red-600">
                {errorMessage(errors.consent?.message)}
              </span>
            ) : null}

            {submitError ? (
              <p role="alert" className="text-sm text-red-600">
                {submitError}
              </p>
            ) : null}

            {/* Pinned to the bottom of the sheet on mobile; inline on desktop. */}
            <div className="bg-surface sticky bottom-0 z-10 -mx-6 mt-2 px-6 pb-6 pt-3 sm:static sm:mx-0 sm:mt-0 sm:bg-transparent sm:p-0">
              <Button
                type="submit"
                variant="accent"
                size="lg"
                className="w-full"
                disabled={isSubmitting}
              >
                {isSubmitting ? t('applyForm.submitting') : t('applyForm.submit')}
              </Button>
            </div>
          </form>
        </div>
      ) : (
        <div className="flex flex-col items-center gap-6 p-6 text-center">
          <div className="relative flex size-40 items-center justify-center">
            <span className="border-success/16 bg-success/4 absolute size-40 rounded-full border" />
            <span className="border-success/16 bg-success/6 absolute size-[130px] rounded-full border" />
            <span className="border-success/16 bg-success/8 absolute size-[100px] rounded-full border" />
            <img src={successEffect} alt="" className="absolute size-full" />
            <span className="bg-success relative flex size-[70px] items-center justify-center rounded-full">
              <img src={successCheck} alt="" className="size-9" />
            </span>
          </div>
          <div className="flex flex-col gap-2">
            <h2 id="apply-modal-title" className="text-text-primary text-2xl font-semibold">
              {t('applyForm.success.title')}
            </h2>
            <p className="text-text-placeholder text-base">{t('applyForm.success.description')}</p>
          </div>
          <Button variant="accent" size="lg" className="w-full" onClick={close}>
            {t('applyForm.success.cta')}
          </Button>
        </div>
      )}
    </dialog>
  );
});
